import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Grid Column Definition Interface
 */
export interface GridColumn {
  key: string;
  label: string;
  translationKey: string;
  type?: 'text' | 'badge' | 'date' | 'custom';
  customRender?: (value: any, row: any) => string;
}

/**
 * Grid Action Interface
 */
export interface GridAction {
  id: string;
  label: string;
  type: string;
  icon?: string;
  condition?: (row: any) => boolean;
  class?: string;
}

/**
 * Reusable generic grid component
 * Dynamically renders columns and actions based on input configuration
 * Can be used for any data table (requests, users, files, etc.)
 */
@Component({
  selector: 'app-common-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './common-grid.component.html',
  styleUrls: ['./common-grid.component.scss']
})
export class CommonGridComponent implements OnInit, OnDestroy {
  @Input() rows: any[] = [];
  @Input() columns: GridColumn[] = [];
  @Input() actions: GridAction[] = [];
  @Input() loading: boolean = false;
  @Input() rowIdentifier: string = 'id';
  @Input() rowIdSelector?: (row: any) => string;
  @Input() emptyStateIcon: string = 'bi-inbox';
  @Input() emptyStateMessageKey: string = 'common.noData';
  @Input() rowClass?: (row: any) => string;
  @Input() enableRowClick: boolean = false;

  // Pagination inputs
  @Input() currentPage: number = 0;
  @Input() pageSize: number = 10;
  @Input() totalRecords: number = 0;
  @Input() pageSizeOptions: number[] = [10, 25, 100];
  @Input() showPagination: boolean = true;

  @Output() actionTriggered = new EventEmitter<{ action: GridAction; row: any }>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  // Language and directionality
  currentLanguage: string = 'en';
  isRTL: boolean = false;
  private destroy$ = new Subject<void>();

  // Expose Math to template
  Math = Math;

  constructor(private translateService: TranslateService) {
    this.initializeLanguage();
  }

  ngOnInit(): void {
    // Listen for language changes
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.currentLanguage = event.lang;
        this.isRTL = event.lang === 'ar';
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeLanguage(): void {
    // Get current language from translate service
    const currentLang = this.translateService.currentLang || this.translateService.defaultLang || 'en';
    this.currentLanguage = currentLang;
    this.isRTL = currentLang === 'ar';
  }

  /**
   * Handle action button click
   */
  onActionClick(action: GridAction, row: any): void {
    this.actionTriggered.emit({ action, row });
  }

  /**
   * Emit a row activation when click navigation is enabled.
   * Ignore the actions cell so its buttons remain independent controls,
   * and ignore a click that ends a text selection.
   */
  onRowClick(event: MouseEvent, row: any): void {
    if (!this.enableRowClick) {
      return;
    }

    if ((event.target as HTMLElement | null)?.closest('.cell-actions')) {
      return;
    }

    if (window.getSelection()?.toString()) {
      return;
    }

    this.rowClick.emit(row);
  }

  /**
   * Check if action should be displayed for this row
   */
  shouldShowAction(action: GridAction, row: any): boolean {
    if (!action.condition) {
      return true;
    }
    return action.condition(row);
  }

  /**
   * Get cell value based on column key
   */
  getCellValue(row: any, columnKey: string): string {
    const value = row[columnKey];
    
    // Check if column has custom render function
    const column = this.columns.find(col => col.key === columnKey);
    if (column?.customRender) {
      return column.customRender(value, row);
    }

    // Handle date formatting
    if (columnKey.toLowerCase().includes('date') && value) {
      return this.formatDate(value);
    }

    return value || '-';
  }

  /**
   * Format date string to dd/mm/yyyy
   * Validates date before formatting
   */
  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      
      // Validate date
      if (isNaN(date.getTime())) {
        return dateString;
      }

      // Format as dd/mm/yyyy
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return dateString || '';
    }
  }

  /**
   * Get status badge class (for request status badges)
   */
  getStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      // Draft & Initial States
      'Draft': 'badge-secondary',
      
      // Success & Completion States
      'Completed': 'badge-success',
      'Closed': 'badge-success',
      'Approved': 'badge-success',
      'Acknowledged': 'badge-success',
      
      // In Progress & Processing States
      'InProgress': 'badge-warning',
      'Submitted': 'badge-warning',
      'UnderGeneration': 'badge-warning',
      'UnderCDAApproval': 'badge-warning',
      'For Approval': 'badge-warning',
      'ForApproval': 'badge-warning',
      
      // Pending States (Awaiting Review/Action)
      'Pending': 'badge-info',
      'PendingWithLDF': 'badge-info',
      'PendingWithLDIRF': 'badge-info',
      'PendingLDMReview': 'badge-info',
      'PendingLDIRReview': 'badge-info',
      'PendingCDAReview': 'badge-info',
      'PendingWithSP': 'badge-info',
      'PendingWithPQM': 'badge-info',
      'PendingWithCDA': 'badge-info',
      'PendingWithSPSH': 'badge-info',
      'PendingAmendment': 'badge-info',
      
      // Rejected & Canceled States
      'Rejected': 'badge-danger',
      'Canceled': 'badge-danger'
    };
    return statusMap[status] || 'badge-secondary';
  }

  /**
   * Get row identifier value
   */
  getRowId(row: any): string {
    if (this.rowIdSelector) {
      return this.rowIdSelector(row);
    }
    return row[this.rowIdentifier] || '';
  }

  /**
   * Track by function for ngFor performance optimization
   * Using arrow function to preserve 'this' context
   */
  trackByRowId = (index: number, row: any): string => {
    return this.getRowId(row);
  };

  /**
   * Get custom row class if provided
   */
  getRowCssClass(row: any): string {
    if (this.rowClass) {
      return this.rowClass(row);
    }
    return '';
  }

  /**
   * Pagination methods
   */
  
  /**
   * Calculate total pages
   */
  getTotalPages(): number {
    if (this.pageSize === 0) return 0;
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  /**
   * Check if there are more pages
   */
  hasNextPage(): boolean {
    return (this.currentPage + 1) < this.getTotalPages();
  }

  /**
   * Check if there is a previous page
   */
  hasPreviousPage(): boolean {
    return this.currentPage > 0;
  }

  /**
   * Handle page change
   */
  onPageChange(pageNumber: number): void {
    if (pageNumber >= 0 && pageNumber < this.getTotalPages()) {
      this.pageChange.emit(pageNumber);
    }
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(newPageSize: number): void {
    this.pageSizeChange.emit(newPageSize);
  }

  /**
   * Get array of page numbers for pagination display
   */
  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const maxDisplayPages = 5;
    const pages: number[] = [];

    if (totalPages <= maxDisplayPages) {
      // Show all pages if less than max
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current page
      const startPage = Math.max(0, this.currentPage - 2);
      const endPage = Math.min(totalPages - 1, this.currentPage + 2);

      // Add first page
      if (startPage > 0) {
        pages.push(0);
      }

      // Add ellipsis if needed
      if (startPage > 1) {
        pages.push(-1); // -1 represents ellipsis
      }

      // Add pages around current
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 2) {
        pages.push(-1); // -1 represents ellipsis
      }

      // Add last page
      if (endPage < totalPages - 1) {
        pages.push(totalPages - 1);
      }
    }

    return pages;
  }
}
